import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { useNavigate } from "react-router-dom";
import {
  CheckCircleOutlined,
  FileTextOutlined,
  SendOutlined,
  BarChartOutlined,
  TeamOutlined,
  SafetyOutlined,
  ArrowRightOutlined,
  MenuOutlined
} from "@ant-design/icons";

import "./qhrset.scss";

function QHRSET() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const handleGetStarted = () => {
    navigate("/signup");
  };

  const handleSignin = () => {
    navigate("/login");
  };

  // Scrolls to the top of the page when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="qhrset-wrapper">

      {/* =====================================================
          HEADER
      ====================================================== */}

      <header className="qhrset-header">

        <div
          className="qhrset-logo"
        >
          <div className="qhrset-logo-main">
            QHR<span>SET</span>
          </div>

          <div className="qhrset-logo-subtitle">
            Candidate Evaluation
          </div>
        </div>

        <nav className="qhrset-header-menu">
          <a href="#features">Features</a>
          <a href="#how-it-works">How It Works</a>
          <a href="#evaluation">Evaluation</a>
          <a href="#contact">Contact</a>
          <a href="/home">QUALLYHIRE AI</a>
        </nav>
        <div
          className="qhrset-hamburger"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <MenuOutlined />
        </div>
        <div className="qhrset-header-actions">
          <Button
            className="qhrset-signin-button"
            onClick={handleSignin}
          >
            Sign In
          </Button>

          <Button
            type="primary"
            className="qhrset-header-start-button"
            onClick={handleGetStarted}
          >
            Get Started
          </Button>
        </div>

      </header>

      <div
        className={`qhrset-mobile-menu ${
          mobileMenuOpen ? "open" : ""
        }`}
      >
        <a href="#features" onClick={() => setMobileMenuOpen(false)}>
          Features
        </a>

        <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)}>
          How It Works
        </a>

        <a href="#pricing" onClick={() => setMobileMenuOpen(false)}>
          Pricing
        </a>

        <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
          Contact
        </a>

        <a href="/home" onClick={() => setMobileMenuOpen(false)}>
          QUALLYHIRE AI
        </a>
      </div>

      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="qhrset-hero">

        <div className="qhrset-hero-content">

          {/* LEFT SIDE */}

          <div className="qhrset-hero-left">

            <div className="qhrset-hero-badge">
              ONLINE CANDIDATE EVALUATION
            </div>

            <h1>
              Evaluate Candidates.
              <span>
                Faster. Smarter. Better.
              </span>
            </h1>

            <p>
              QHRSET helps companies evaluate candidates online
              with structured assessments, automated scoring and
              detailed candidate insights.
            </p>

            <div className="qhrset-hero-buttons">

              <Button
                type="primary"
                size="large"
                onClick={handleGetStarted}
              >
                Get Started
                <ArrowRightOutlined />
              </Button>

              <Button
                size="large"
                onClick={() => {
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({
                      behavior: "smooth",
                    });
                }}
              >
                Request Demo
              </Button>

            </div>

            <div className="qhrset-hero-points">

              <span>
                <CheckCircleOutlined />
                Online Assessments
              </span>

              <span>
                <CheckCircleOutlined />
                Automated Evaluation
              </span>

              <span>
                <CheckCircleOutlined />
                Candidate Scoring
              </span>

            </div>

          </div>


          {/* RIGHT SIDE - CANDIDATE SCORE */}

          <div className="qhrset-hero-right">

            <div className="qhrset-score-card">

              <div className="qhrset-score-header">

                <div>
                  <span>
                    Candidate Evaluation
                  </span>

                  <h3>
                    Evaluation Result
                  </h3>
                </div>

                <div className="qhrset-score-status">
                  Completed
                </div>

              </div>


              <div className="qhrset-score-main">

                <div className="qhrset-score-circle">

                  <strong>86</strong>

                  <span>/100</span>

                </div>

                <div className="qhrset-score-info">

                  <h4>
                    Excellent Candidate
                  </h4>

                  <p>
                    Overall Evaluation Score
                  </p>

                </div>

              </div>


              <div className="qhrset-score-items">

                <div className="qhrset-score-item">

                  <span>
                    Technical Skills
                  </span>

                  <strong>
                    92%
                  </strong>

                </div>

                <div className="qhrset-score-bar">
                  <span style={{ width: "92%" }} />
                </div>


                <div className="qhrset-score-item">

                  <span>
                    Problem Solving
                  </span>

                  <strong>
                    84%
                  </strong>

                </div>

                <div className="qhrset-score-bar">
                  <span style={{ width: "84%" }} />
                </div>


                <div className="qhrset-score-item">

                  <span>
                    Communication
                  </span>

                  <strong>
                    82%
                  </strong>

                </div>

                <div className="qhrset-score-bar">
                  <span style={{ width: "82%" }} />
                </div>

              </div>


              <div className="qhrset-score-footer">

                <span>
                  Recommendation
                </span>

                <strong>
                  Shortlist Candidate
                </strong>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          TRUST / INTRO
      ====================================================== */}

      <section className="qhrset-intro">

        <div className="qhrset-section-heading">

          <div className="qhrset-section-badge">
            WHY QHRSET
          </div>

          <h2>
            Make Candidate Evaluation
            <span> Simple and Structured</span>
          </h2>

          <p>
            Stop relying only on resumes and subjective interviews.
            Give every candidate a consistent evaluation and make
            better hiring decisions with measurable results.
          </p>

        </div>

      </section>


      {/* =====================================================
          FEATURES
      ====================================================== */}

      <section
        id="features"
        className="qhrset-features"
      >

        <div className="qhrset-section-heading">

          <div className="qhrset-section-badge">
            FEATURES
          </div>

          <h2>
            Everything You Need to
            <span> Evaluate Candidates</span>
          </h2>

        </div>


        <div className="qhrset-feature-grid">

          <div className="qhrset-feature-card">

            <div className="qhrset-feature-icon">
              <FileTextOutlined />
            </div>

            <h3>
              Online Assessments
            </h3>

            <p>
              Create structured online evaluations and send them
              directly to candidates.
            </p>

          </div>


          <div className="qhrset-feature-card">

            <div className="qhrset-feature-icon">
              <SendOutlined />
            </div>

            <h3>
              Send Evaluation
            </h3>

            <p>
              Quickly send an evaluation link to candidates and
              track their completion status.
            </p>

          </div>


          <div className="qhrset-feature-card">

            <div className="qhrset-feature-icon">
              <BarChartOutlined />
            </div>

            <h3>
              Candidate Scoring
            </h3>

            <p>
              Automatically calculate candidate scores and compare
              evaluation results.
            </p>

          </div>


          <div className="qhrset-feature-card">

            <div className="qhrset-feature-icon">
              <TeamOutlined />
            </div>

            <h3>
              Candidate Insights
            </h3>

            <p>
              Get a clear view of candidate performance and identify
              the strongest candidates.
            </p>

          </div>


          <div className="qhrset-feature-card">

            <div className="qhrset-feature-icon">
              <CheckCircleOutlined />
            </div>

            <h3>
              Structured Evaluation
            </h3>

            <p>
              Evaluate every candidate using a consistent and
              standardized process.
            </p>

          </div>


          <div className="qhrset-feature-card">

            <div className="qhrset-feature-icon">
              <SafetyOutlined />
            </div>

            <h3>
              Secure Evaluation
            </h3>

            <p>
              Keep candidate evaluation data organized and securely
              available to your hiring team.
            </p>

          </div>

        </div>

      </section>


      {/* =====================================================
          HOW IT WORKS
      ====================================================== */}

      <section
        id="how-it-works"
        className="qhrset-how-it-works"
      >

        <div className="qhrset-section-heading">

          <div className="qhrset-section-badge">
            HOW IT WORKS
          </div>

          <h2>
            Evaluate Candidates in
            <span> Four Simple Steps</span>
          </h2>

        </div>


        <div className="qhrset-workflow">

          <div className="qhrset-workflow-card">

            <div className="qhrset-workflow-number">
              01
            </div>

            <h3>
              Create Evaluation
            </h3>

            <p>
              Create an online evaluation based on the skills
              and requirements of the position.
            </p>

          </div>


          <div className="qhrset-workflow-card">

            <div className="qhrset-workflow-number">
              02
            </div>

            <h3>
              Send to Candidate
            </h3>

            <p>
              Send the evaluation link directly to the candidate
              through your hiring workflow.
            </p>

          </div>


          <div className="qhrset-workflow-card">

            <div className="qhrset-workflow-number">
              03
            </div>

            <h3>
              Candidate Completes
            </h3>

            <p>
              Candidates complete the assessment online at
              their convenience.
            </p>

          </div>


          <div className="qhrset-workflow-card">

            <div className="qhrset-workflow-number">
              04
            </div>

            <h3>
              Review Score
            </h3>

            <p>
              Review the candidate score and evaluation results
              to make a better hiring decision.
            </p>

          </div>

        </div>

      </section>


      {/* =====================================================
          EVALUATION SECTION
      ====================================================== */}

      <section
        id="evaluation"
        className="qhrset-evaluation"
      >

        <div className="qhrset-evaluation-content">

          <div className="qhrset-evaluation-left">

            <div className="qhrset-section-badge">
              BETTER DECISIONS
            </div>

            <h2>
              Turn Candidate Performance
              <span> Into Actionable Insights</span>
            </h2>

            <p>
              QHRSET transforms candidate assessments into
              easy-to-understand scores that help hiring teams
              quickly identify candidates who deserve the next
              stage of the hiring process.
            </p>

            <div className="qhrset-evaluation-points">

              <div>
                <CheckCircleOutlined />
                Consistent candidate evaluation
              </div>

              <div>
                <CheckCircleOutlined />
                Easy-to-understand candidate scores
              </div>

              <div>
                <CheckCircleOutlined />
                Faster screening decisions
              </div>

              <div>
                <CheckCircleOutlined />
                Better hiring visibility
              </div>

            </div>

          </div>


          <div className="qhrset-evaluation-right">

            <div className="qhrset-mini-dashboard">

              <div className="qhrset-dashboard-header">

                <div>
                  <span>
                    Evaluation Dashboard
                  </span>

                  <h3>
                    Candidate Performance
                  </h3>
                </div>

              </div>


              <div className="qhrset-candidate-row">

                <div className="qhrset-candidate-info">

                  <div className="qhrset-avatar">
                    AS
                  </div>

                  <div>
                    <strong>
                      Amit Sharma
                    </strong>

                    <span>
                      Software Engineer
                    </span>
                  </div>

                </div>

                <div className="qhrset-candidate-score">
                  91
                </div>

              </div>


              <div className="qhrset-candidate-row">

                <div className="qhrset-candidate-info">

                  <div className="qhrset-avatar">
                    RK
                  </div>

                  <div>
                    <strong>
                      Rahul Kumar
                    </strong>

                    <span>
                      Senior Developer
                    </span>
                  </div>

                </div>

                <div className="qhrset-candidate-score">
                  86
                </div>

              </div>


              <div className="qhrset-candidate-row">

                <div className="qhrset-candidate-info">

                  <div className="qhrset-avatar">
                    PS
                  </div>

                  <div>
                    <strong>
                      Priya Shah
                    </strong>

                    <span>
                      Software Developer
                    </span>
                  </div>

                </div>

                <div className="qhrset-candidate-score">
                  79
                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CTA
      ====================================================== */}

      <section
        id="contact"
        className="qhrset-cta"
      >

        <div className="qhrset-cta-content">

          <h2>
            Ready to Evaluate Candidates
            <span> More Effectively?</span>
          </h2>

          <p>
            Start using QHRSET to bring structure, speed and
            measurable insights to your candidate evaluation process.
          </p>

          <div className="qhrset-cta-buttons">

            <Button
              type="primary"
              size="large"
              onClick={handleGetStarted}
            >
              Get Started
              <ArrowRightOutlined />
            </Button>

            <Button
              size="large"
              onClick={handleSignin}
            >
              Sign In
            </Button>

          </div>

        </div>

      </section>


      {/* =====================================================
          FOOTER
      ====================================================== */}

      <footer className="qhrset-footer">

        <div className="qhrset-footer-content">

          <div className="qhrset-footer-brand">

            <div className="qhrset-logo-main">
              QHR<span>SET</span>
            </div>

            <p>
              Online candidate evaluation made simple,
              structured and measurable.
            </p>

          </div>


          <div className="qhrset-footer-links">

            <div>
              <h4>
                Product
              </h4>

              <a href="#features">
                Features
              </a>

              <a href="#how-it-works">
                How It Works
              </a>

              <a href="#evaluation">
                Evaluation
              </a>

            </div>


            <div>
              <h4>
                Company
              </h4>

              <a href="/home">
                QuallyHire AI
              </a>

              <a href="#contact">
                Contact
              </a>

            </div>

          </div>

        </div>


        <div className="qhrset-footer-bottom">

          © {new Date().getFullYear()} QuallyHire AI. All
          rights reserved.

        </div>

      </footer>

    </div>
  );
}

export default QHRSET;